import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    compose,
    defaultProps,
    mapProps,
    onlyUpdateForKeys,
    shouldUpdate,
    withHandlers,
    withProps,
    withState
} from 'recompose';
import { difference, equals, head, isNil, omit, path, pick, pipe, prop, unless } from 'ramda';
import { usePrevious } from 'js/hooks/usePrevious';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useAppSelector, useUserSelector, useDeviceSelector } from 'js/store/hooks';
import { DISPLAY_MODE, GAME_SIZES } from 'js/components/common/GamesView/constants';
import { usePortletConfig, usePortletSelector } from 'js/components/system/Portlet';
import GamesView from 'js/components/main/Games/GamesView';
import { CategoryContext } from 'js/components/main/Games/context';
import { CATEGORY_TYPE } from 'js/components/main/Games/constants';
import { ViewMoreWithExpanded } from 'js/components/main/Games/Category/ViewMore';
import { trackGameCategoryActiveSlideChange } from 'js/components/main/Games/trackingApi';
import { useCategoryName } from '../hooks/useCategoryName';
import style from './index.scss';

export const CategoryBare = ({
    gameCodes,
    setCategoryRef,
    displayMode,
    gameSize,
    expanded,
    scrollIntoView,
    className = '',
    displayName,
    showTitle,
    slidesLength,
    setSlidesLength,
    onActiveSlideChange,
    mostPlayedGame,
    t,
    isShowMobileViewMore,
    setIsShowViewMore,
    categoryEl,
    isGamesRendered,
    isFirstCategory,
    ...category
}) => {
    const { cid, type, roundIcon } = category;
    const getFormattedCategoryName = useCategoryName();
    const formatCategoryName = getFormattedCategoryName({ gameName: mostPlayedGame, categoryName: displayName, cid });
    const categoryRef = useRef();
    const username = useUserSelector(path(['userDetails', 'firstName']));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const recommendedArr = ['recommended-all-dynamic-category-recommended-all', 'recommended-live-dynamic-category-recommended-live'];
    const isRecommendedCategory = Boolean(recommendedArr.filter(name => name === cid).toString());
    const hiddenRecommendedCategory = !isLoggedIn && isRecommendedCategory;
    const isPinned = type === CATEGORY_TYPE.PINNED;
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const topCategory = roundIcon === 'top';
    const isTopCategory = topCategory && !isDesktop;
    const isMobileSpecialCategory = !isDesktop && roundIcon;
    const { showViewMore } = usePortletConfig();
    const [isIntersecting, setIsIntersecting] = useState(false);

    const categoryClsName = useMemo(() => [
        className,
        style.category,
        isPinned ? style.pinned : '',
        expanded ? style.expanded : ''
    ].join(' '), [className, expanded, isPinned]);
    const previousGamesCodes = usePrevious(gameCodes);
    const lastAddedGameCode = useMemo(
        () =>
            unless(
                isNil,
                pipe(
                    difference(gameCodes),
                    head
                )
            )(previousGamesCodes),
        [gameCodes, previousGamesCodes]
    );

    useEffect(() => {
        const newObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && isGamesRendered || entry.isIntersecting && isFirstCategory) {
                    setIsIntersecting(entry.isIntersecting);
                }
            },
            {
                root: null,
                rootMargin: '128px',
                threshold: 0.9
            }
        );
        if (categoryEl) {
            newObserver.observe(categoryEl);
        }

        return () => newObserver.disconnect();
    }, [categoryEl]);

    return (
        <CategoryContext.Provider
            value={{
                ...category,
                gameSize,
                lastAddedGameCode,
                isRecommendedCategory,
                hiddenRecommendedCategory }}>
            { !hiddenRecommendedCategory && <div ref={setCategoryRef} className={categoryClsName} id={cid}>
                {showTitle && (
                    <h3 className={style.heading}>

                        <span>{formatCategoryName}
                            {isRecommendedCategory && (
                                <span>{`, ${username}`}</span>
                            )}
                        </span>

                        {(showViewMore && slidesLength > 1 || !isDesktop && isShowMobileViewMore) && (
                            <ViewMoreWithExpanded
                                t={t}
                                cid={cid}
                                expanded={expanded}
                                count={gameCodes.length}
                                gameCodes={gameCodes}
                                displayName={displayName}
                                isRecommendedCategory={isRecommendedCategory}
                                formatCategoryName={formatCategoryName}
                                mostPlayedGame={mostPlayedGame}
                            />
                        )}
                    </h3>
                )}
                {isIntersecting && <GamesView
                    displayName={displayName}
                    displayMode={displayMode}
                    gameSize={gameSize}
                    gameCodes={gameCodes}
                    scrollIntoView={scrollIntoView}
                    setSlidesLength={setSlidesLength}
                    setIsShowViewMore={setIsShowViewMore}
                    categoryRef={categoryRef}
                    onActiveSlideChange={onActiveSlideChange}
                    expanded={expanded}
                    showDivider={isPinned}
                    isTopCategory={isTopCategory}
                    isMobileSpecialCategory={isMobileSpecialCategory}
                    mostPlayedGame={mostPlayedGame}
                />}
                {!isIntersecting && <div className={style.emptyCategoryWrapper}/>}
            </div>
            }
        </CategoryContext.Provider>
    );
};

export const Category = compose(
    onlyUpdateForKeys(['type', 'gameCodes']),
    defaultProps({ showTitle: true }),
    withTranslations,
    withState('categoryEl', 'setCategoryEl', null),
    withState('slidesLength', 'setSlidesLength', 0),
    withState('isShowMobileViewMore', 'setIsShowViewMore', true),
    withProps(() => useAppSelector(pick(['headerHeight']))),
    withHandlers(() => {
        let categoryRef;

        return {
            setCategoryRef: ({ setCategoryEl }) => ref => {
                categoryRef = ref;
                setCategoryEl(ref);
            },
            scrollIntoView: ({ headerHeight }) => () => {
                const { documentElement, body } = document;
                const { top: categoryTop } = categoryRef.getBoundingClientRect();
                const top = categoryTop + (documentElement.scrollTop || body.scrollTop) - headerHeight;

                window.scrollTo(0, top);
            }
        };
    }),
    withProps(({ mostPlayedGameCode }) => ({
        mostPlayedGame: useAppSelector(path(['games', mostPlayedGameCode, 'name'])),
        reactPortletId: prop('reactPortletId', usePortletConfig())
    })),
    mapProps(({ type, cid, ...other }) => {
        const isDesktop = useDeviceSelector(prop('isDesktop'));
        const { expandedCategory, feedName: gameFeed } = usePortletSelector(pick(['expandedCategory', 'feedName']));
        const { roundIcon } = other;
        const expanded = expandedCategory === cid;
        const featured = roundIcon === 'featured';
        const mediumGameSize = featured || cid === 'favorites' || cid === 'recentlyplayed';

        const displayMode = expanded
            ? DISPLAY_MODE.GRID
            : isDesktop
                ? DISPLAY_MODE.PAGING
                : DISPLAY_MODE.SLIDER;
        const gameSize = type === CATEGORY_TYPE.PINNED
            ? GAME_SIZES.LARGE
            : isDesktop || !isDesktop && mediumGameSize
                ? GAME_SIZES.MEDIUM
                : GAME_SIZES.SMALL;

        return {
            ...other,
            cid,
            type,
            expanded,
            gameFeed,
            displayMode,
            gameSize
        };
    }),
    withHandlers(() => ({
        onActiveSlideChange: ({ reactPortletId, gameFeed, name }) => ({ direction }) => {
            const elementClicked = direction === 'left' ? 'previous' : 'next';

            trackGameCategoryActiveSlideChange(reactPortletId, gameFeed, name, elementClicked);
        }
    })),
    shouldUpdate((props, nextProps) => {
        const isNotEqualExpanded = props.expanded !== nextProps.expanded;
        const isNotEqualGameCodes = !equals(props.gameCodes, nextProps.gameCodes);
        const isNotEqualDisplayMode = props.displayMode !== nextProps.displayMode;
        const isNotEqualSlidesLength = props.slidesLength !== nextProps.slidesLength;
        const isNotEqualViewMore = props.isShowMobileViewMore !== nextProps.isShowMobileViewMore;
        const isNotEqualCategoryEl = props.categoryEl !== nextProps.categoryEl;
        const isNotEqualIsGamesRendered = props.isGamesRendered !== nextProps.isGamesRendered;
        const isNotEqualIsFirstCategory = props.isFirstCategory !== nextProps.isFirstCategory;

        return isNotEqualExpanded ||
            isNotEqualGameCodes ||
            isNotEqualDisplayMode ||
            isNotEqualSlidesLength ||
            isNotEqualViewMore ||
            isNotEqualCategoryEl ||
            isNotEqualIsGamesRendered ||
            isNotEqualIsFirstCategory;
    }),
    mapProps(omit(['gameFeed']))
)(CategoryBare);
